import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faCheck,
  faTriangleExclamation,
  faXmark,
  faRotateRight,
  faChevronDown,
  faChevronUp,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import { useUploads } from "./useUploads";
import {
  STATUS,
  isActive,
  summarize,
  cancel,
  retry,
  dismiss,
  clearFinished,
  readInterrupted,
  clearInterrupted,
} from "./uploadManager";

export const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const statusIcon = (entry) => {
  switch (entry.status) {
    case STATUS.DONE:
      return <FontAwesomeIcon icon={faCheck} style={{ color: "#20a56e" }} />;
    case STATUS.FAILED:
    case STATUS.TOO_BIG:
      return <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#ce3d3d" }} />;
    case STATUS.CANCELLED:
      return <FontAwesomeIcon icon={faBan} style={{ color: "#9299a8" }} />;
    default:
      return <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#038edc" }} />;
  }
};

/**
 * Floating card, bottom right, listing every upload on the page. Mounted
 * once in the backend layout so it is visible whatever modal or route the
 * user is on. It also reports uploads that a page unload cut off.
 */
const UploadStatusWidget = () => {
  const { t } = useTranslation();
  const uploads = useUploads();
  const [collapsed, setCollapsed] = useState(false);
  const [interrupted, setInterrupted] = useState([]);

  useEffect(() => {
    setInterrupted(readInterrupted());
  }, []);

  if (uploads.length === 0 && interrupted.length === 0) return null;

  const summary = summarize(uploads);
  const percent = Math.round(summary.progress * 100);

  let headline;
  if (summary.active > 0) {
    headline = t("Backend.Upload.Widget.Uploading", {
      defaultValue: "Uploading {{count}} of {{total}} · {{percent}}%",
      count: summary.active,
      total: summary.active + summary.done,
      percent,
    });
  } else if (summary.failed > 0) {
    headline = t("Backend.Upload.Widget.Failed", {
      defaultValue: "{{count}} upload(s) failed",
      count: summary.failed,
    });
  } else if (summary.done > 0) {
    headline = t("Backend.Upload.Widget.Done", {
      defaultValue: "{{count}} upload(s) finished",
      count: summary.done,
    });
  } else {
    headline = t("Backend.Upload.Widget.Interrupted", { defaultValue: "Interrupted uploads" });
  }

  const statusLabel = (entry) => {
    switch (entry.status) {
      case STATUS.QUEUED:
        return t("Backend.Upload.Status.Queued", { defaultValue: "waiting" });
      case STATUS.UPLOADING:
        return `${Math.round(entry.progress * 100)}%`;
      case STATUS.SAVING:
        return t("Backend.Upload.Status.Saving", { defaultValue: "saving" });
      case STATUS.DONE:
        return t("Backend.Upload.Status.Done", { defaultValue: "done" });
      case STATUS.TOO_BIG:
        return t("Backend.Upload.Status.TooBig", { defaultValue: "too big" });
      case STATUS.CANCELLED:
        return t("Backend.Upload.Status.Cancelled", { defaultValue: "cancelled" });
      default:
        return t("Backend.Upload.Status.Failed", { defaultValue: "failed" });
    }
  };

  const closeWidget = () => {
    clearFinished();
    clearInterrupted();
    setInterrupted([]);
  };

  return (
    <div className="uploadWidget" role="status" aria-live="polite">
      <div className="uploadWidgetHeader">
        <span className="uploadWidgetIcon">
          {summary.active > 0 ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : summary.failed > 0 || interrupted.length > 0 ? (
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#ce3d3d" }} />
          ) : (
            <FontAwesomeIcon icon={faCheck} style={{ color: "#20a56e" }} />
          )}
        </span>
        <span className="uploadWidgetTitle">{headline}</span>
        <button
          type="button"
          className="uploadWidgetButton"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? "expand" : "collapse"}
        >
          <FontAwesomeIcon icon={collapsed ? faChevronUp : faChevronDown} />
        </button>
        {summary.active === 0 && (
          <button
            type="button"
            className="uploadWidgetButton"
            onClick={closeWidget}
            aria-label={t("Backend.Modal.Closebutton", { defaultValue: "Close" })}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
      {summary.active > 0 && (
        <div className="uploadProgress uploadWidgetProgress">
          <div className="uploadProgressBar" style={{ width: `${percent}%` }} />
        </div>
      )}
      {!collapsed && (
        <div className="uploadWidgetBody">
          {interrupted.length > 0 && (
            <div className="uploadWidgetInterrupted">
              <p>
                {t("Backend.Upload.Widget.InterruptedHint", {
                  defaultValue:
                    "These uploads were cut off when the page was closed and were not saved. Please upload them again:",
                })}
              </p>
              <ul>
                {interrupted.map((name, index) => (
                  <li key={`${name}_${index}`}>{name}</li>
                ))}
              </ul>
            </div>
          )}
          {uploads.map((entry) => (
            <div className="uploadWidgetRow" key={entry.id}>
              <span className="uploadWidgetRowIcon">{statusIcon(entry)}</span>
              <span className="uploadWidgetRowName" title={entry.name}>
                {entry.name}
              </span>
              <span className="uploadWidgetRowMeta">
                {formatBytes(entry.size)} · {statusLabel(entry)}
              </span>
              {isActive(entry) && (
                <button
                  type="button"
                  className="uploadWidgetButton"
                  title={t("Backend.Upload.Cancel", { defaultValue: "Cancel upload" })}
                  onClick={() => cancel(entry.id)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
              {(entry.status === STATUS.FAILED || entry.status === STATUS.CANCELLED) && (
                <button
                  type="button"
                  className="uploadWidgetButton"
                  title={t("Backend.Upload.Retry", { defaultValue: "Retry" })}
                  onClick={() => retry(entry.id)}
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>
              )}
              {!isActive(entry) && (
                <button
                  type="button"
                  className="uploadWidgetButton"
                  title={t("Backend.Upload.Remove", { defaultValue: "Remove from list" })}
                  onClick={() => dismiss(entry.id)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
              {entry.status === STATUS.UPLOADING && (
                <div className="uploadProgress uploadWidgetRowProgress">
                  <div className="uploadProgressBar" style={{ width: `${Math.round(entry.progress * 100)}%` }} />
                </div>
              )}
            </div>
          ))}
          {summary.active > 0 && (
            <p className="uploadWidgetHint">
              {t("Backend.Upload.KeepPageOpen", {
                defaultValue: "Keep this browser tab open until the uploads are finished.",
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadStatusWidget;
